<template>
  <div class="top-bar">
    <div class="top-bar-left">
      <button class="mobile-menu-btn" @click="$emit('toggle-mobile-menu')">
        <i class="bi bi-list"></i>
      </button>
      <h1>{{ pageTitle }}</h1>
    </div>
    <div class="user-info">
      <div class="user-details">
        <span class="user-name">{{ userFullName }}</span>
        <span
          class="user-role clickable"
          @click="toggleUserMenu"
        >
          {{ userRole }}
          <i class="bi bi-chevron-down ms-1"></i>
        </span>

        <!-- User Menu Dropdown -->
        <div v-if="showUserMenu" class="user-menu-dropdown" @click.stop>
          <!-- Admin-only options -->
          <template v-if="isAdmin">
            <div class="user-menu-item" @click="openImpersonationModal">
              <i class="bi bi-person-badge me-2"></i>
              Impersonate User
            </div>
            <div class="user-menu-divider"></div>
            <router-link to="/admin/users" class="user-menu-item" @click="showUserMenu = false">
              <i class="bi bi-people me-2"></i>
              Manage Users
            </router-link>
            <router-link to="/admin/settings" class="user-menu-item" @click="showUserMenu = false">
              <i class="bi bi-gear me-2"></i>
              Settings
            </router-link>
            <div class="user-menu-divider"></div>
          </template>
          <!-- Available to all users -->
          <router-link to="/settings" class="user-menu-item" @click="showUserMenu = false">
            <i class="bi bi-gear me-2"></i>
            User Settings
          </router-link>
          <div class="user-menu-item" @click="openGdprModal">
            <i class="bi bi-shield-check me-2"></i>
            Privacy & GDPR
          </div>
        </div>
      </div>
      <div class="avatar-wrapper">
        <img
          :src="userAvatar"
          :alt="`${userFullName} Avatar`"
          class="user-avatar"
          @error="onImageError"
        >
        <span
          v-if="pendingRequestsCount > 0"
          class="pending-badge"
          v-tooltip="`${pendingRequestsCount} pending connection request${pendingRequestsCount > 1 ? 's' : ''}`"
          @click="goToPendingRequests"
        >
          {{ pendingRequestsCount > 9 ? '9+' : pendingRequestsCount }}
        </span>
      </div>
    </div>
  </div>

  <!-- Impersonation Modal -->
  <ImpersonationModal
    :show="showImpersonationModal"
    @close="showImpersonationModal = false"
    @impersonate="handleImpersonate"
  />

  <!-- GDPR Consent Modal -->
  <div v-if="showGdprModal" class="gdpr-modal-overlay" @click.self="closeGdprModal">
    <div class="gdpr-modal">
      <div class="gdpr-modal-header">
        <h3><i class="bi bi-shield-check me-2"></i>Privacy & GDPR Consent</h3>
        <button class="gdpr-modal-close" @click="closeGdprModal">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="gdpr-modal-body">
        <div v-if="gdprLoading" class="gdpr-loading">
          <div class="spinner-border text-primary" role="status"></div>
          <p>Loading preferences...</p>
        </div>

        <template v-else>
          <div class="gdpr-intro">
            <p>
              In compliance with the General Data Protection Regulation (GDPR), we need your consent to process your personal data.
              Please review and manage your consent preferences below. You can change these settings at any time.
            </p>
          </div>

          <div class="consent-items">
            <!-- Data Processing Consent -->
            <div class="consent-item" :class="{ 'consent-given': gdprConsent.dataProcessing }">
              <div class="consent-header">
                <div class="consent-icon">
                  <i class="bi bi-database"></i>
                </div>
                <div class="consent-info">
                  <h4>Data Processing</h4>
                  <p>Allow us to process your personal data for employment purposes, including payroll, performance management, and internal communications.</p>
                </div>
                <div class="consent-toggle">
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      v-model="gdprConsent.dataProcessing"
                      @change="updateGdprConsent('data_processing_consent', gdprConsent.dataProcessing)"
                    >
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
              <div v-if="gdprConsentDates.dataProcessing" class="consent-date">
                <i class="bi bi-clock-history"></i>
                Last updated: {{ formatConsentDate(gdprConsentDates.dataProcessing) }}
              </div>
            </div>

            <!-- Marketing Consent -->
            <div class="consent-item" :class="{ 'consent-given': gdprConsent.marketing }">
              <div class="consent-header">
                <div class="consent-icon">
                  <i class="bi bi-megaphone"></i>
                </div>
                <div class="consent-info">
                  <h4>Marketing Communications</h4>
                  <p>Receive newsletters, company updates, and promotional materials about internal programs and initiatives.</p>
                </div>
                <div class="consent-toggle">
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      v-model="gdprConsent.marketing"
                      @change="updateGdprConsent('marketing_consent', gdprConsent.marketing)"
                    >
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
              <div v-if="gdprConsentDates.marketing" class="consent-date">
                <i class="bi bi-clock-history"></i>
                Last updated: {{ formatConsentDate(gdprConsentDates.marketing) }}
              </div>
            </div>

            <!-- Analytics Consent -->
            <div class="consent-item" :class="{ 'consent-given': gdprConsent.analytics }">
              <div class="consent-header">
                <div class="consent-icon">
                  <i class="bi bi-graph-up"></i>
                </div>
                <div class="consent-info">
                  <h4>Analytics & Usage Tracking</h4>
                  <p>Allow collection of usage data to improve platform features and user experience. This data is anonymized and used for internal improvements only.</p>
                </div>
                <div class="consent-toggle">
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      v-model="gdprConsent.analytics"
                      @change="updateGdprConsent('analytics_consent', gdprConsent.analytics)"
                    >
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
              <div v-if="gdprConsentDates.analytics" class="consent-date">
                <i class="bi bi-clock-history"></i>
                Last updated: {{ formatConsentDate(gdprConsentDates.analytics) }}
              </div>
            </div>

            <!-- Third Party Consent -->
            <div class="consent-item" :class="{ 'consent-given': gdprConsent.thirdParty }">
              <div class="consent-header">
                <div class="consent-icon">
                  <i class="bi bi-share"></i>
                </div>
                <div class="consent-info">
                  <h4>Third-Party Data Sharing</h4>
                  <p>Allow sharing of necessary data with authorized third-party service providers (e.g., benefits administrators, training platforms).</p>
                </div>
                <div class="consent-toggle">
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      v-model="gdprConsent.thirdParty"
                      @change="updateGdprConsent('third_party_consent', gdprConsent.thirdParty)"
                    >
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
              <div v-if="gdprConsentDates.thirdParty" class="consent-date">
                <i class="bi bi-clock-history"></i>
                Last updated: {{ formatConsentDate(gdprConsentDates.thirdParty) }}
              </div>
            </div>
          </div>

          <div class="gdpr-footer">
            <div class="gdpr-links">
              <a href="#" class="gdpr-link">
                <i class="bi bi-file-text"></i> Privacy Policy
              </a>
              <a href="#" class="gdpr-link">
                <i class="bi bi-download"></i> Download My Data
              </a>
              <a href="#" class="gdpr-link text-danger">
                <i class="bi bi-trash"></i> Request Data Deletion
              </a>
            </div>
            <p class="gdpr-note">
              <i class="bi bi-info-circle"></i>
              For any questions about your data privacy rights, please contact HR or our Data Protection Officer.
            </p>
          </div>
        </template>
      </div>

      <div class="gdpr-modal-footer">
        <button class="btn-close-modal" @click="closeGdprModal">Close</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useApi } from '@/composables/useApi';
import { getAvatarUrl, createAvatarErrorHandler, AVATAR_SIZES } from '@/composables/useAvatar';
import ImpersonationModal from '@/components/shared/ImpersonationModal.vue';

defineEmits(['toggle-mobile-menu']);

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const api = useApi();

// Pending connection requests count
const pendingRequestsCount = ref(0);

// User menu and impersonation state
const showUserMenu = ref(false);
const showImpersonationModal = ref(false);
const showGdprModal = ref(false);

// GDPR Consent state
const gdprConsent = ref({
  dataProcessing: false,
  marketing: false,
  analytics: false,
  thirdParty: false
});

const gdprConsentDates = ref({
  dataProcessing: null,
  marketing: null,
  analytics: null,
  thirdParty: null
});

const gdprLoading = ref(false);

// Check if user is admin
const isAdmin = computed(() => {
  const user = authStore.currentUser;
  return user?.role === 'admin';
});

// Toggle user menu dropdown
function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value;
}

// Open impersonation modal
function openImpersonationModal() {
  showUserMenu.value = false;
  showImpersonationModal.value = true;
}

// Handle successful impersonation
function handleImpersonate(user) {
  // Page will reload automatically from the composable
}

// Open GDPR modal
async function openGdprModal() {
  showUserMenu.value = false;
  gdprLoading.value = true;
  showGdprModal.value = true;

  try {
    const response = await api.get('/api/user/gdpr-consent');
    if (response.consent) {
      gdprConsent.value = {
        dataProcessing: response.consent.data_processing?.consent || false,
        marketing: response.consent.marketing?.consent || false,
        analytics: response.consent.analytics?.consent || false,
        thirdParty: response.consent.third_party?.consent || false
      };
      gdprConsentDates.value = {
        dataProcessing: response.consent.data_processing?.date || null,
        marketing: response.consent.marketing?.date || null,
        analytics: response.consent.analytics?.date || null,
        thirdParty: response.consent.third_party?.date || null
      };
    }
  } catch (error) {
    console.error('Error loading GDPR consent:', error);
  } finally {
    gdprLoading.value = false;
  }
}

// Close GDPR modal
function closeGdprModal() {
  showGdprModal.value = false;
}

// Update GDPR consent
async function updateGdprConsent(consentType, value) {
  try {
    const payload = { [consentType]: value };
    const response = await api.patch('/api/user/gdpr-consent', payload);

    // Update local dates from response
    if (response.consent) {
      const consentData = response.consent;
      gdprConsentDates.value = {
        dataProcessing: consentData.data_processing?.date || gdprConsentDates.value.dataProcessing,
        marketing: consentData.marketing?.date || gdprConsentDates.value.marketing,
        analytics: consentData.analytics?.date || gdprConsentDates.value.analytics,
        thirdParty: consentData.third_party?.date || gdprConsentDates.value.thirdParty
      };
    }
  } catch (error) {
    console.error('Error updating GDPR consent:', error);
    // Revert the toggle on error
    const keyMap = {
      'data_processing_consent': 'dataProcessing',
      'marketing_consent': 'marketing',
      'analytics_consent': 'analytics',
      'third_party_consent': 'thirdParty'
    };
    const key = keyMap[consentType];
    if (key) {
      gdprConsent.value[key] = !value;
    }
  }
}

// Format consent date for display
function formatConsentDate(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Close menu when clicking outside
function handleClickOutside(event) {
  const userMenu = document.querySelector('.user-menu-dropdown');
  const userRole = document.querySelector('.user-role');

  if (userMenu && userRole &&
      !userMenu.contains(event.target) &&
      !userRole.contains(event.target)) {
    showUserMenu.value = false;
  }
}

// Fetch pending connection requests count
async function fetchPendingRequestsCount() {
  try {
    const response = await api.get('/api/connections/stats');
    pendingRequestsCount.value = response.pendingReceived || 0;
  } catch (err) {
    console.error('Failed to fetch pending requests count:', err);
  }
}

// Navigate to pending requests filter in Workspace -> My Network section
function goToPendingRequests() {
  router.push({ path: '/workspace', query: { filter: 'pending' }, hash: '#my-network' });
}

// Add click outside listener
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  fetchPendingRequestsCount();
});

// Remove click outside listener
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Compute page title based on current route
const pageTitle = computed(() => {
  if (route.meta.title) {
    return route.meta.title;
  }

  // Fallback based on route name
  const titleMap = {
    'company-news': 'Company',
    'company-financials': 'Financials',
    'company-compliance': 'Compliance',
    'company-resources': 'Resources',
    'company-orgchart': 'Organization Chart',
    'team-directory': 'My Team',
    'team-timesheet-approvals': 'Timesheet Approvals',
    'team-leave-approvals': 'Leave Approvals',
    'team-performance-reviews': 'My Performance',
    'projects-allocation': 'Resource Allocation',
    'projects-revenue': 'Revenue Tracker',
    'projects-timeline': 'Projects Timeline',
    'workspace': 'My Workspace',
    'workspace-timesheets': 'My Timesheets',
    'workspace-leave': 'Leave Requests',
    'workspace-documents': 'My Documents',
    'admin-dashboard': 'System Dashboard',
    'admin-users': 'User Management',
    'admin-access': 'Access Control',
    'admin-audit': 'Audit Logs',
    'admin-settings': 'Settings',
  };

  return titleMap[route.name] || 'Dashboard';
});

// Get user info from auth store
const userFullName = computed(() => {
  const user = authStore.currentUser;
  if (!user) return 'User';

  if (user.first_name || user.last_name) {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }

  return user.email || 'User';
});

const userRole = computed(() => {
  const user = authStore.currentUser;
  return user?.role || 'user';
});

const userAvatar = computed(() => {
  return getAvatarUrl(authStore.currentUser, { size: AVATAR_SIZES.sm });
});

// Handle image loading errors
const onImageError = createAvatarErrorHandler(authStore.currentUser, AVATAR_SIZES.sm);
</script>

<style scoped>
.top-bar {
  background: white;
  padding: 20px 30px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.top-bar-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: #333;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.mobile-menu-btn:hover {
  background: #f0f0f0;
}

.top-bar h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.pending-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #f39c12;
  color: white;
  font-size: 10px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.pending-badge:hover {
  background: #e67e22;
  transform: scale(1.1);
}

.user-details {
  text-align: right;
  position: relative;
}

.user-name {
  font-weight: 600;
  color: #333;
  display: block;
  font-size: 14px;
}

.user-role {
  font-size: 12px;
  color: #6c757d;
  text-transform: capitalize;
  position: relative;
}

.user-role.clickable {
  cursor: pointer;
  transition: color 0.2s;
  user-select: none;
}

.user-role.clickable:hover {
  color: #7B8FD4;
}

.user-menu-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-menu-item {
  padding: 12px 16px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  text-decoration: none;
}

.user-menu-item:first-child {
  border-radius: 8px 8px 0 0;
}

.user-menu-item:last-child {
  border-radius: 0 0 8px 8px;
}

.user-menu-item:hover {
  background-color: #f8f9fa;
  color: #7B8FD4;
}

.user-menu-divider {
  height: 1px;
  background-color: #e9ecef;
  margin: 4px 0;
}

/* GDPR Modal Styles */
.gdpr-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.gdpr-modal {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.gdpr-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #7B8FD4 0%, #5a6eb8 100%);
  color: white;
}

.gdpr-modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.gdpr-modal-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.gdpr-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.gdpr-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.gdpr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  color: #6b7280;
}

.gdpr-intro {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);
  border-radius: 12px;
  border-left: 4px solid #7B8FD4;
}

.gdpr-intro p {
  margin: 0;
  color: #374151;
  line-height: 1.6;
  font-size: 0.9rem;
}

.consent-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.consent-item {
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.3s ease;
}

.consent-item.consent-given {
  background: #f0fdf4;
  border-color: #86efac;
}

.consent-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.consent-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #7B8FD4 0%, #5a6eb8 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.consent-icon i {
  font-size: 1.1rem;
  color: white;
}

.consent-item.consent-given .consent-icon {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
}

.consent-info {
  flex: 1;
}

.consent-info h4 {
  margin: 0 0 0.25rem 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
}

.consent-info p {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.4;
}

.consent-toggle {
  flex-shrink: 0;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #d1d5db;
  transition: 0.3s;
  border-radius: 26px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.toggle-switch input:checked + .toggle-slider {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(22px);
}

.consent-date {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #e5e7eb;
  font-size: 0.7rem;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.consent-date i {
  font-size: 0.8rem;
}

/* GDPR Footer */
.gdpr-footer {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
}

.gdpr-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.gdpr-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  background: #f3f4f6;
  border-radius: 6px;
  color: #4b5563;
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.gdpr-link:hover {
  background: #e5e7eb;
  color: #1f2937;
}

.gdpr-link.text-danger {
  color: #dc2626;
}

.gdpr-link.text-danger:hover {
  background: #fef2f2;
  color: #b91c1c;
}

.gdpr-link i {
  font-size: 0.9rem;
}

.gdpr-note {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.gdpr-note i {
  color: #7B8FD4;
  flex-shrink: 0;
  margin-top: 2px;
}

.gdpr-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
}

.btn-close-modal {
  padding: 0.6rem 1.5rem;
  background: linear-gradient(135deg, #7B8FD4 0%, #5a6eb8 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close-modal:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(123, 143, 212, 0.4);
}

/* Responsive adjustments for GDPR modal */
@media (max-width: 640px) {
  .gdpr-modal {
    max-height: 95vh;
    margin: 10px;
  }

  .consent-header {
    flex-wrap: wrap;
  }

  .consent-toggle {
    width: 100%;
    margin-top: 0.75rem;
    display: flex;
    justify-content: flex-end;
  }

  .gdpr-links {
    flex-direction: column;
  }

  .gdpr-link {
    justify-content: center;
  }
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .top-bar {
    padding: 15px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50; /* Above content but below modals */
  }

  .mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .top-bar h1 {
    font-size: 18px;
  }

  .user-info {
    gap: 10px;
  }

  .user-details {
    display: none;
  }

  .user-avatar {
    width: 36px;
    height: 36px;
  }
}

@media (max-width: 576px) {
  .top-bar {
    padding: 12px 10px;
  }

  .top-bar-left {
    gap: 10px;
  }

  .top-bar h1 {
    font-size: 16px;
  }

  .mobile-menu-btn {
    font-size: 22px;
    padding: 6px;
  }
}
</style>
